import type { MakeStylesHook, StyleRules } from '@egr/xbox/utils/tss-react';

import * as React from 'react';

import Header from './components/AppBar/Header';
import { AppWrapper } from './components/AppWrapper';
import ErrorMessage from './components/ErrorMessage';
import FAQ from './components/FAQ';
import { DelayedLoadingTile } from './components/Progress';
import Toast from './components/Toast';
import { LocalFileDropTarget } from './components/debug/LocalFileDropTarget';
import Footer from './components/footer/Footer';
import { appStore } from './stores/AppStore';
import { fullscreenViewerStore } from './stores/FullScreenViewerStore';
import { interactorSelectionStore } from './stores/InteractorSelectionStore';
import { ProgressPanel } from './viewers/ViewerBabylon/components/ProgressPanel';
import { ViewerContainer } from './viewers/ViewerContainer/ViewerContainer';

import { cookieDialogStore } from '@egr/xbox/egr-gui-dialogs/cookieDialog/CookieDialogStore';
import { setCustomDimension } from '@egr/xbox/telemetry/MatomoUtils';
import { layoutInformation } from '@egr/xbox/utils/Layout';
import { android, inIframe } from '@egr/xbox/utils/UserAgent';
import { setupSentry } from '@egr/xbox/utils/errors/Error';
import { PersistentStore } from '@egr/xbox/utils/store/PersistentStore';
import { makeStyles } from '@egr/xbox/utils/tss-react';

function styles(): StyleRules<'main'> {
    return {
        main: {
            display: 'flex',
            flex: 1,
            minHeight: 0,
            zIndex: 1,
            '& a': {
                color: '#f7a600',
                textDecoration: 'none',
                '::after': {
                    content: '" \xbb"',
                },
            },
        },
    };
}

const useStyles: MakeStylesHook<'main'> = makeStyles({ name: 'App' })(styles);

const App: React.FC = () => {
    const { classes } = useStyles();
    const { embed } = appStore.useState(['embed', 'urlInfo']);
    const { dialogOpen } = interactorSelectionStore.useState(['dialogOpen']);
    const { dialogSubmitted } = cookieDialogStore.useState(['submitDialog', 'dialogSubmitted', 'tempTelemetry', 'tempDiagnostics', 'tempYoutube']);
    const { smallLayout } = layoutInformation.useState(['smallLayout']);
    const { fullScreen } = fullscreenViewerStore.useState(['fullScreen']);

    React.useEffect(() => {
        async function initialize() {
            onResize();

            setCustomDimension(1, embed ? 'embedded' : 'direct call');
            if (!inIframe()) {
                await cookieDialogStore.waitForSubmit();
            }

            PersistentStore.enableSaving();
            setupSentry();
            addEvents();
        }
        void initialize();

        return () => {
            removeEvents();
        };
    }, []);

    if (embed) {
        return (
            <AppWrapper>
                <ProgressPanel />
                {dialogSubmitted && <DelayedLoadingTile />}
                <ErrorMessage />
                <ViewerContainer />
            </AppWrapper>
        );
    }

    return (
        <AppWrapper>
            {(!dialogOpen || !smallLayout) && !fullScreen && <Header />}
            <div className={classes.main}>
                <ProgressPanel />
                {dialogSubmitted && <DelayedLoadingTile />}
                <ErrorMessage />
                <ViewerContainer />
                <FAQ />
            </div>
            {!smallLayout && !fullScreen && <Footer />}
            <Toast />
            <LocalFileDropTarget />
        </AppWrapper>
    );
};

function onResize(): void {
    const root: HTMLElement | null = document.getElementById('root');
    if (root !== null) {
        const w: number = window.innerWidth;
        const h: number = window.innerHeight;
        const width: string = android ? '100%' : w + 'px';
        const height: string = android ? '100dvh' : h + 'px';
        // Android: 100% is short when the address bar is hidden...

        root.style.height = height;
        root.style.maxHeight = height;

        root.style.width = width;
        root.style.maxWidth = width;
    }
}
function handler() {
    window.setTimeout(
        () => {
            window.dispatchEvent(new Event('customfullscreenchange'));
            window.dispatchEvent(new UIEvent('resize')); // triggers bubbles repositioning
        },
        500 // waits for UI to be redrawn
    );
}

function addEvents() {
    if (document.addEventListener) {
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);

        document.addEventListener('fullscreenchange', handler, false);
        document.addEventListener('mozfullscreenchange', handler, false);
        document.addEventListener('MSFullscreenChange', handler, false);
        document.addEventListener('webkitfullscreenchange', handler, false);
    }
}
function removeEvents() {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);

    document.removeEventListener('fullscreenchange', handler, false);
    document.removeEventListener('mozfullscreenchange', handler, false);
    document.removeEventListener('MSFullscreenChange', handler, false);
    document.removeEventListener('webkitfullscreenchange', handler, false);
}

export default App;
